import React, {useEffect} from 'react';
import {useNavigate} from "react-router";
import {useParams} from "react-router-dom";
import {concepts} from "fearless-common";
import {FormikValues} from "formik";
import {Alert, Loading, useTitle} from "fearless-common-ui";
import {Pages} from "../components/layout";
import Description from "../components/Description";
import {routes} from "../utils";
import BackendForm from "../components/forms/BackendForm";
import SignUpFields, {signUpInitialValues} from "./partials/SignUpFields";
import {backendEndpoints} from "../libs/backendEndpoints";
import useAuth from "../components/hooks/useAuth";
import useGet from "../components/hooks/useGet";
import { FloatingLabelField } from '../components/forms';

interface CompleteAccountFields extends FormikValues {
    hash: string;
    user_id: string;
    first_name: string,
    last_name: string,
    organisation: string,
    group_name: string,
    password: string,
    password_copy: string,
    username: string,
}


/**
 * Page on which the user can complete their account after they have
 * been invited via email.
 */
export default function CompleteAccount() {
    useTitle('Complete your account');
    const navigate = useNavigate()
    const { userId, hash } = useParams()
    const { user: currentlyLoggedIn, logout, saveToken } = useAuth();

    const { data, isLoading } = useGet<{ completed: boolean; loginLink: string; email: string; teamName: string }>({
      endpoint: userId ? backendEndpoints.userAccountWasCompleted(userId) : '',
    });

    useEffect(() => {
        // Log out the currently logged in account if the user wants to complete an(other) account.
        if (currentlyLoggedIn) {
            logout();
            window.location.reload();
        }
    }, [currentlyLoggedIn]);

    useEffect(() => {
        if (data?.completed) {
            window.location.href = data.loginLink || routes.login;
        }
    }, [data?.completed]);

    return (
        <Pages.Centred width="xs" padded>
            <h1 className="gradient-color mb-5 text-center">Welcome!</h1>
            {isLoading && <Loading />}
            {!data?.completed && (
              <>
                <Description className="mb-5 text-center">
                  You have been invited to join {<b>{data?.teamName}</b> || `a ${concepts.Group}`} via your email.
                    <br />
                    Please fill in the details below to complete your
                  account.
                </Description>
                  {isLoading ? <Loading /> : (
                    !userId || !hash ? <Alert>The link was corrupted. Please ask for a new link for the person who has shared this link with you</Alert> : (
                    <BackendForm<CompleteAccountFields, { token?: string }>
                      initialValues={{
                        hash,
                        user_id: userId,
                        prefilled_email: data?.email,
                        ...signUpInitialValues.Account,
                        ...signUpInitialValues.UsernamePassword,
                      }}
                      onSuccess={({ data }) => {
                          if (data?.token) {
                            saveToken(data.token);
                            window.location.href = routes.landing;
                          } else {
                            navigate(routes.completeAccountSuccess);
                          }
                      }}
                      targetEndpoint={backendEndpoints.signUp}
                      targetMethod="PUT"
                    >
                      <h3>Create login</h3>
                      <FloatingLabelField name="prefilled_email" label="Email used for invite" disabled />
                      <SignUpFields.UsernamePassword checkDuplicate />
                      <br/>
                      <SignUpFields.Account showEmail={false} />
                    </BackendForm>
                  ))}
              </>
            )}
        </Pages.Centred>
    );
}
